import React, { useRef, useMemo, useState, useEffect, useCallback, memo } from 'react';
import type { ExecutionStep, MatrixStatePayload, ArrayStatePayload } from '../../../core/types/rust-engine-interfaces';
import { StepType } from '../../../core/types/rust-engine-interfaces';

interface MatrixVisualizerProps {
  currentStep: ExecutionStep | null;
  algorithmId?: string;
}

type MatrixSignature = 'sudoku' | 'rat-maze' | 'n-queens' | 'knights-tour' | 'dp-coin' | 'dp-knapsack' | 'dp-lcs' | 'dp-edit' | 'dp-lis' | 'dp-mcm' | 'dp-lps' | 'dp-fib' | 'dp-floyd' | 'miller-rabin' | 'dp' | 'none';

function detectMatrixSignature(algorithmId: string): MatrixSignature {
  const id = algorithmId.toLowerCase();
  if (id.includes('sudoku')) return 'sudoku';
  if (id.includes('rat') && id.includes('maze')) return 'rat-maze';
  if (id.includes('maze')) return 'rat-maze';
  if (id.includes('queen')) return 'n-queens';
  if (id.includes('knight')) return 'knights-tour';
  if (id.includes('floyd')) return 'dp-floyd';
  if (id.includes('matrix-chain') || id.includes('mcm')) return 'dp-mcm';
  if (id.includes('palindromic-subsequence') || id.includes('lps')) return 'dp-lps';
  if (id.includes('coin')) return 'dp-coin';
  if (id.includes('knapsack')) return 'dp-knapsack';
  if (id.includes('lcs') || id.includes('longest-common')) return 'dp-lcs';
  if (id.includes('edit-distance')) return 'dp-edit';
  if (id.includes('lis') || id.includes('longest-increasing')) return 'dp-lis';
  if (id.includes('fibonacci')) return 'dp-fib';
  if (id.includes('miller') || id.includes('rabin')) return 'miller-rabin';
  if (id.includes('dp') || id.includes('partition') || id.includes('word-break')) return 'dp';
  return 'none';
}

function signatureLabel(sig: MatrixSignature): string {
  switch (sig) {
    case 'sudoku': return 'SUDOKU';
    case 'rat-maze': return 'MAZE';
    case 'n-queens': return 'N-QUEENS';
    case 'knights-tour': return 'KNIGHT';
    case 'dp-coin': return 'COIN-CHANGE';
    case 'dp-knapsack': return 'KNAPSACK';
    case 'dp-lcs': return 'LCS';
    case 'dp-edit': return 'EDIT-DIST';
    case 'dp-lis': return 'LIS';
    case 'dp-mcm': return 'MCM';
    case 'dp-lps': return 'LPS';
    case 'dp-fib': return 'FIB';
    case 'dp-floyd': return 'FLOYD';
    case 'miller-rabin': return 'MILLER-RABIN';
    case 'dp': return 'DP';
    default: return '';
  }
}

function signatureColor(sig: MatrixSignature): string {
  switch (sig) {
    case 'sudoku': return '#3b82f6';
    case 'rat-maze': return '#22c55e';
    case 'n-queens': return '#fbbf24';
    case 'knights-tour': return '#a855f7';
    case 'dp-coin': return '#22d3ee';
    case 'dp-knapsack': return '#f97316';
    case 'dp-lcs': return '#a78bfa';
    case 'dp-edit': return '#ec4899';
    case 'dp-lis': return '#4ade80';
    case 'dp-mcm': return '#fbbf24';
    case 'dp-lps': return '#06b6d4';
    case 'dp-fib': return '#60a5fa';
    case 'dp-floyd': return '#f472b6';
    case 'miller-rabin': return '#f59e0b';
    case 'dp': return '#60a5fa';
    default: return '#64748b';
  }
}

// ── Data extraction ───────────────────────────────────────────────────────────

interface MatrixData {
  data: (number | string)[][];
  rows: number;
  cols: number;
  currentCell: { row: number; col: number } | null;
  highlightedCells: { row: number; col: number }[];
  rowHeaders: string[] | null;
  colHeaders: string[] | null;
  stepType: StepType | null;
  explanation: string;
}

function hasMatrixPayload(step: ExecutionStep): step is ExecutionStep & { payload: MatrixStatePayload } {
  return step.payload.type === 'matrix';
}

function isGenericMatrixPayload(payload: unknown): payload is { type: 'generic'; data: Record<string, unknown> } {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    (payload as Record<string, unknown>).type === 'generic' &&
    typeof (payload as Record<string, unknown>).data === 'object' &&
    (payload as Record<string, unknown>).data !== null
  );
}

function extractMatrix(step: ExecutionStep | null): MatrixData | null {
  if (!step) return null;
  const base = { stepType: step.stepType ?? null, explanation: step.metadata?.explanation ?? '' };

  if (hasMatrixPayload(step)) {
    const mp   = step.payload;
    if (!mp.data?.length) return null;
    const meta = mp.metadata ?? {};
    return {
      ...base,
      data: mp.data, rows: mp.rows, cols: mp.cols,
      currentCell:      meta.currentCell      ?? null,
      highlightedCells: meta.highlightedCells ?? [],
      rowHeaders:       meta.rowHeaders       ?? null,
      colHeaders:       meta.colHeaders       ?? null,
    };
  }

  if (isGenericMatrixPayload(step.payload)) {
    const d   = step.payload.data;
    const raw = d.matrix ?? d.data ?? d.board ?? d.grid ?? d.dp ?? null;
    if (Array.isArray(raw) && Array.isArray(raw[0])) {
      const data = (raw as unknown[][]).map((row) =>
        (row as unknown[]).map((v) => (typeof v === 'number' || typeof v === 'string') ? v : 0))
      ;
      const rows = data.length;
      const cols = data[0]?.length ?? 0;
      if (rows > 0 && cols > 0)
        return { ...base, data, rows, cols, currentCell: null, highlightedCells: [], rowHeaders: null, colHeaders: null };
    }
  }

  // Handle 1D array payload (e.g. LIS DP array) — render as single-row matrix
  if (step.payload.type === 'array') {
    const ap = step.payload as ArrayStatePayload;
    const items = ap.items ?? [];
    if (items.length === 0) return null;
    const data = [items.map(item => typeof item.value === 'number' ? item.value : (typeof item.value === 'string' ? item.value : 0))];
    const meta = ap.metadata ?? {};
    const highlightedCells: { row: number; col: number }[] = (meta.highlightIndices ?? []).map((i: number) => ({ row: 0, col: i }));
    const currentCell = meta.currentIndex !== undefined && meta.currentIndex >= 0 ? { row: 0, col: meta.currentIndex } : null;
    return {
      ...base,
      data,
      rows: 1,
      cols: items.length,
      currentCell,
      highlightedCells,
      rowHeaders: null,
      colHeaders: items.map((_, i) => String(i)),
    };
  }

  return null;
}

// ── 4-stop heat-map gradient ──────────────────────────────────────────────────
//
// n=0.00 → deep navy    #0d1526
// n=0.25 → indigo-dark  #1e1b4b → #2d2080
// n=0.55 → teal         #0e7490
// n=0.80 → sky          #0ea5e9
// n=1.00 → sky-bright   #38bdf8
//
// Text fades from slate-500 at low to slate-100 at high.

type RGB = [number, number, number];
const HEAT_STOPS: { t: number; rgb: RGB; text: RGB }[] = [
  { t: 0.00, rgb: [13,  21,  38 ], text: [51,  65,  85 ] },
  { t: 0.25, rgb: [30,  27,  75 ], text: [71,  85,  105] },
  { t: 0.50, rgb: [14,  116, 144], text: [148, 163, 184] },
  { t: 0.75, rgb: [14,  165, 233], text: [226, 232, 240] },
  { t: 1.00, rgb: [56,  189, 248], text: [241, 245, 249] },
];

function lerpRGB(a: RGB, b: RGB, f: number): RGB {
  return [
    Math.round(a[0] + f * (b[0] - a[0])),
    Math.round(a[1] + f * (b[1] - a[1])),
    Math.round(a[2] + f * (b[2] - a[2])),
  ];
}

function heatColor(n: number): { bg: string; text: string } {
  const t = Math.max(0, Math.min(1, n));
  for (let i = 1; i < HEAT_STOPS.length; i++) {
    const lo = HEAT_STOPS[i - 1]!;
    const hi = HEAT_STOPS[i]!;
    if (t <= hi.t) {
      const f    = (t - lo.t) / (hi.t - lo.t);
      const rgb  = lerpRGB(lo.rgb, hi.rgb, f);
      const txt  = lerpRGB(lo.text, hi.text, f);
      return {
        bg:   `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`,
        text: `rgb(${txt[0]},${txt[1]},${txt[2]})`,
      };
    }
  }
  const last = HEAT_STOPS[HEAT_STOPS.length - 1]!;
  return { bg: `rgb(${last.rgb.join(',')})`, text: `rgb(${last.text.join(',')})` };
}

// ── Step type badge ───────────────────────────────────────────────────────────

function badgeLabel(type: StepType | string | null): { label: string; color: string } {
  switch (type) {
    case StepType.Initialization: return { label: 'INIT', color: '#60a5fa' };
    case StepType.Comparison:     return { label: 'CMP',  color: '#a78bfa' };
    case StepType.Update:         return { label: 'UPD',  color: '#34d399' };
    case StepType.StateChange:    return { label: 'CHG',  color: '#a855f7' };
    case StepType.Completion:     return { label: 'DONE', color: '#f59e0b' };
    case StepType.Decision:       return { label: 'DEC',  color: '#fb923c' };
    case StepType.Swap:           return { label: 'SWAP', color: '#f97316' };
    default:                      return { label: 'STEP', color: '#64748b' };
  }
}

// ── Virtual-windowing thresholds ────────────────────────────────────────────────

/** Total cell count above which virtual windowing activates. */
const VIRTUAL_THRESHOLD = 400;
/** Overscan: extra rows/cols rendered beyond the visible viewport edge. */
const OVERSCAN = 2;

// ── Component ─────────────────────────────────────────────────────────────────

export const MatrixVisualizer: React.FC<MatrixVisualizerProps> = memo(({ currentStep, algorithmId = '' }) => {
  const containerRef  = useRef<HTMLDivElement>(null);
  const scrollRef      = useRef<HTMLDivElement>(null);
  const [size, setSize]           = useState({ w: 800, h: 560 });
  const [scrollPos, setScrollPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const e = entries[0];
      if (!e) return;
      setSize({ w: Math.max(200, e.contentRect.width), h: Math.max(120, e.contentRect.height) });
    });
    ro.observe(el);
    const r = el.getBoundingClientRect();
    setSize({ w: Math.max(200, r.width), h: Math.max(120, r.height) });
    return () => ro.disconnect();
  }, []);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setScrollPos({ top: el.scrollTop, left: el.scrollLeft });
  }, []);

  const matrix = extractMatrix(currentStep);

  if (!matrix) {
    return (
      <div ref={containerRef} className="flex h-full w-full items-center justify-center"
        style={{ color: '#475569' }}>
        <div className="text-center space-y-1">
          <p className="text-sm font-medium">No matrix data</p>
          <p className="text-xs">Execute a DP or matrix algorithm to begin</p>
        </div>
      </div>
    );
  }

  const { data, rows, cols, currentCell, highlightedCells, rowHeaders, colHeaders, stepType, explanation } = matrix;

  const hasRowH  = rowHeaders !== null && rowHeaders.length === rows;
  const hasColH  = colHeaders !== null && colHeaders.length === cols;
  const gridCols = cols + (hasRowH ? 1 : 0);
  const gridRows = rows + (hasColH ? 1 : 0);

  const matrixSig = detectMatrixSignature(algorithmId);
  const isSudoku = matrixSig === 'sudoku' && rows === 9 && cols === 9;
  const isMaze = matrixSig === 'rat-maze';
  const isKnights = matrixSig === 'knights-tour';

  // Detect N-Queens board: square, only 0/1, no headers.
  const isQueensBoard = useMemo(
    () => (matrixSig === 'n-queens' || (rows === cols && !hasRowH && !hasColH && data.every(r => r.every(v => v === 0 || v === 1)))),
    [data, rows, cols, hasRowH, hasColH, matrixSig],
  );

  // ── Cell sizing ───────────────────────────────────────────────────────────

  const { cell, gap } = useMemo(() => {
    // Chrome overhead: top-pad(10) + bottom-pad(8) + badge-row(28) + explanation(22 if present)
    const CHROME_H = 46 + (explanation ? 22 : 0);
    const PAD_W    = 24;
    const avW = Math.max(1, size.w - PAD_W);
    const avH = Math.max(1, size.h - CHROME_H);
    const gp  = Math.max(2, Math.min(4, Math.floor(Math.min(avW / gridCols, avH / gridRows) * 0.07)));
    const wB  = Math.floor((avW - gp * (gridCols + 1)) / gridCols);
    const hB  = Math.floor((avH - gp * (gridRows + 1)) / gridRows);
    const c   = Math.min(Math.max(18, Math.min(wB, hB)), 96);
    return { cell: c, gap: gp };
  }, [size, gridCols, gridRows, explanation]);

  const fontSize = Math.max(9, Math.min(cell * 0.32, 16));
  const radius   = Math.max(3, Math.round(cell * 0.12));

  // ── State sets ────────────────────────────────────────────────────────────

  const hlSet  = useMemo(() => new Set(highlightedCells.map((c) => `${c.row}:${c.col}`)), [highlightedCells]);
  const curKey = currentCell ? `${currentCell.row}:${currentCell.col}` : null;

  const globalMax = useMemo(() => {
    let m = 0;
    data.forEach((row) => row.forEach((v) => { if (typeof v === 'number' && Number.isFinite(v) && v > m) m = v; }));
    return m;
  }, [data]);

  // ── Cell style ────────────────────────────────────────────────────────────
  // All properties that animate are always present (transition targets).
  // This guarantees the browser cross-fades both background AND color smoothly.

  const cellStyle = (ri: number, ci: number, val: number | string): React.CSSProperties => {
    const key   = `${ri}:${ci}`;
    const isCur = curKey === key;
    const isHl  = hlSet.has(key);
    const isStr = typeof val === 'string';
    const numVal = typeof val === 'number' ? val : NaN;
    const isInf = !Number.isFinite(numVal);
    const isNeg = numVal < 0 && !isInf;
    const base  = { borderRadius: radius, transition: 'background 0.18s ease, color 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease' };

    if (isCur) {
      return {
        ...base,
        border: '2px solid #f59e0b',
        background: 'rgba(245,158,11,0.28)',
        color: '#fef3c7',
        boxShadow: '0 0 18px 3px rgba(245,158,11,0.50), inset 0 1px 0 rgba(255,255,255,0.10)',
      };
    }
    if (isHl) {
      return {
        ...base,
        border: '1.5px solid #60a5fa',
        background: 'rgba(96,165,250,0.20)',
        color: '#dbeafe',
        boxShadow: '0 0 8px 1px rgba(96,165,250,0.25)',
      };
    }
    if (isStr) {
      return { ...base, border: '1px solid #1e293b', background: 'rgb(13,21,38)', color: '#94a3b8', boxShadow: 'none' };
    }
    if (isInf) {
      return { ...base, border: '1px solid #1e293b', background: 'rgb(13,21,38)', color: '#475569', boxShadow: 'none' };
    }
    if (isNeg) {
      return { ...base, border: '1px solid #3f1127', background: 'rgb(28,10,22)', color: '#fb7185', boxShadow: 'none' };
    }
    if (numVal === 0 || globalMax === 0) {
      return { ...base, border: '1px solid #1e293b', background: 'rgb(13,21,38)', color: '#334155', boxShadow: 'none' };
    }

    const norm     = Math.min(numVal / globalMax, 1);
    const { bg, text } = heatColor(norm);
    return {
      ...base,
      border: `1px solid rgba(255,255,255,${0.03 + norm * 0.10})`,
      background: bg,
      color: text,
      boxShadow: norm > 0.65 ? `0 0 6px 1px rgba(56,189,248,${(norm - 0.65) * 0.5})` : 'none',
    };
  };

  const headerStyle: React.CSSProperties = {
    borderRadius: radius,
    border: '1px solid #1e3a5f',
    background: 'rgba(10,22,40,0.95)',
    color: '#475569',
    fontSize: Math.max(7, fontSize * 0.80),
    fontWeight: 700,
    letterSpacing: '0.04em',
    transition: 'color 0.15s',
  };

  const badge = badgeLabel(stepType);

  // ── Virtual windowing ─────────────────────────────────────────────────────

  const useVirtual = rows * cols > VIRTUAL_THRESHOLD;
  const stride     = cell + gap;

  const virtualWindow = useMemo(() => {
    if (!useVirtual) return null;
    const visRowCount = Math.ceil(size.h / stride) + OVERSCAN * 2;
    const visColCount = Math.ceil(size.w / stride) + OVERSCAN * 2;
    const rowStart = Math.max(0, Math.floor(scrollPos.top  / stride) - OVERSCAN);
    const colStart = Math.max(0, Math.floor(scrollPos.left / stride) - OVERSCAN);
    return {
      rowStart,
      rowEnd:   Math.min(rows, rowStart + visRowCount),
      colStart,
      colEnd:   Math.min(cols, colStart + visColCount),
    };
  }, [useVirtual, rows, cols, stride, size, scrollPos]);

  const totalGridW = gridCols * stride + gap;
  const totalGridH = gridRows * stride + gap;

  // ── Dependency arrows (DP algorithms) ──────────────────────────────────────
  // Draw arrows from highlightedCells to currentCell using an SVG overlay.
  // Only active when there are highlighted cells and a current cell.

  const depArrows = useMemo(() => {
    if (!currentCell || highlightedCells.length === 0) return null;
    const headerOffset = (hasColH ? stride : 0) + gap;
    const rowHeaderOffset = (hasRowH ? stride : 0) + gap;
    const cx = rowHeaderOffset + currentCell.col * stride + cell / 2;
    const cy = headerOffset + currentCell.row * stride + cell / 2;

    return highlightedCells.map((hc, i) => {
      const hx = rowHeaderOffset + hc.col * stride + cell / 2;
      const hy = headerOffset + hc.row * stride + cell / 2;
      const dx = cx - hx;
      const dy = cy - hy;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < 1) return null;
      // Trim arrow to cell boundaries
      const trimR = cell * 0.55;
      const nx = dx / dist;
      const ny = dy / dist;
      const sx = hx + nx * trimR;
      const sy = hy + ny * trimR;
      const ex = cx - nx * trimR;
      const ey = cy - ny * trimR;
      // Curved arrow with control point offset
      const midX = (sx + ex) / 2;
      const midY = (sy + ey) / 2;
      const perpX = -ny * 12;
      const perpY = nx * 12;
      return (
        <path
          key={`dep-${i}`}
          d={`M ${sx} ${sy} Q ${midX + perpX} ${midY + perpY} ${ex} ${ey}`}
          fill="none"
          stroke="#60a5fa"
          strokeWidth={1.5}
          strokeDasharray="3 2"
          opacity={0.65}
          markerEnd="url(#mv-arr-dep)"
        />
      );
    }).filter(Boolean);
  }, [currentCell, highlightedCells, stride, cell, hasColH, hasRowH]);

  // ── Render ────────────────────────────────────────────────────────────────

  const renderCell = (ri: number, ci: number, val: number | string, extraStyle?: React.CSSProperties) => {
    const st      = cellStyle(ri, ci, val);
    const isInf   = typeof val === 'number' && !Number.isFinite(val);
    const isStr   = typeof val === 'string';
    let display: string;
    if (isQueensBoard) {
      display = val === 1 ? '♛' : '·';
    } else if (isKnights) {
      display = typeof val === 'number' && val > 0 ? String(val) : (val === 0 ? '·' : '');
    } else if (isMaze) {
      display = val === 1 ? '' : val === 0 ? '·' : (val === 2 ? '◆' : String(val));
    } else if (isSudoku) {
      display = val === 0 ? '' : String(val);
    } else if (isStr) {
      display = String(val);
    } else {
      display = val === Infinity ? '∞' : String(val);
    }
    const queenStyle: React.CSSProperties = (isQueensBoard && val === 1)
      ? { fontSize: Math.max(10, cell * 0.55), color: '#fbbf24', background: 'rgba(30,15,5,0.95)',
          border: '1px solid rgba(251,191,36,0.5)', boxShadow: '0 0 8px 1px rgba(251,191,36,0.35)' }
      : {};

    // Sudoku 3x3 box border: thicker border on box boundaries
    const sudokuStyle: React.CSSProperties = isSudoku
      ? {
          borderTop: ri % 3 === 0 ? '2px solid #3b82f6' : '1px solid #1e293b',
          borderLeft: ci % 3 === 0 ? '2px solid #3b82f6' : '1px solid #1e293b',
          borderBottom: ri === rows - 1 ? '2px solid #3b82f6' : '1px solid #1e293b',
          borderRight: ci === cols - 1 ? '2px solid #3b82f6' : '1px solid #1e293b',
        }
      : {};

    // Maze wall/path styling
    const mazeStyle: React.CSSProperties = isMaze
      ? val === 0
        ? { background: 'rgb(13,21,38)', border: '1px solid #1e293b', color: '#334155' }
        : val === 1
        ? { background: 'rgb(30,27,75)', border: '1px solid #312e81', color: '#6366f1' }
        : val === 2
        ? { background: 'rgba(34,197,94,0.25)', border: '1.5px solid #22c55e', color: '#4ade80',
            boxShadow: '0 0 8px 1px rgba(34,197,94,0.30)' }
        : {}
      : {};

    // Knight's tour: show step number with color gradient
    const knightStyle: React.CSSProperties = isKnights && typeof val === 'number' && val > 0
      ? {
          background: `rgba(168,85,247,${Math.min(0.15 + val / 50, 0.5)})`,
          border: '1px solid rgba(168,85,247,0.4)',
          color: '#e9d5ff',
        }
      : {};

    // MCM: diagonal cells (i==j) are base case = 0, highlight them
    const mcmStyle: React.CSSProperties = (matrixSig === 'dp-mcm' && ri === ci)
      ? { background: 'rgba(251,191,36,0.12)', border: '1.5px solid rgba(251,191,36,0.4)', color: '#fbbf24' }
      : {};

    // LPS: diagonal cells (i==j) = 1 (base case), highlight them
    const lpsStyle: React.CSSProperties = (matrixSig === 'dp-lps' && ri === ci)
      ? { background: 'rgba(6,182,212,0.12)', border: '1.5px solid rgba(6,182,212,0.4)', color: '#22d3ee' }
      : {};

    // Floyd-Warshall: diagonal (i==j) = 0, highlight with pink tint
    const floydStyle: React.CSSProperties = (matrixSig === 'dp-floyd' && ri === ci)
      ? { background: 'rgba(244,114,182,0.12)', border: '1.5px solid rgba(244,114,182,0.4)', color: '#f472b6' }
      : {};

    // LCS: first row/col = 0 (base case), highlight with purple tint
    const lcsStyle: React.CSSProperties = (matrixSig === 'dp-lcs' && (ri === 0 || ci === 0))
      ? { background: 'rgba(167,139,250,0.10)', border: '1px solid rgba(167,139,250,0.3)', color: '#a78bfa' }
      : {};

    // Edit distance: first row = col index, first col = row index (base cases)
    const editStyle: React.CSSProperties = (matrixSig === 'dp-edit' && (ri === 0 || ci === 0))
      ? { background: 'rgba(236,72,153,0.10)', border: '1px solid rgba(236,72,153,0.3)', color: '#ec4899' }
      : {};

    // Knapsack: first row = 0 (base case), highlight
    const knapStyle: React.CSSProperties = (matrixSig === 'dp-knapsack' && ri === 0)
      ? { background: 'rgba(249,115,22,0.10)', border: '1px solid rgba(249,115,22,0.3)', color: '#f97316' }
      : {};

    // Coin change: first row = INF (base case), highlight
    const coinStyle: React.CSSProperties = (matrixSig === 'dp-coin' && ri === 0)
      ? { background: 'rgba(34,211,238,0.10)', border: '1px solid rgba(34,211,238,0.3)', color: '#22d3ee' }
      : {};

    // Miller-Rabin: verdict column (last col) highlighted amber
    const mrStyle: React.CSSProperties = (matrixSig === 'miller-rabin' && ci === cols - 1 && ri > 0)
      ? { background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.35)', color: '#f59e0b' }
      : {};

    const len = display.length;
    const dynamicFontSize = len > 3 ? Math.max(7, fontSize - (len - 3) * 1.2) : fontSize;

    return (
      <div
        key={`${ri}:${ci}`}
        className="flex items-center justify-center font-semibold select-none overflow-hidden"
        style={{ width: cell, height: cell, fontSize: dynamicFontSize, ...st, ...queenStyle, ...sudokuStyle, ...mazeStyle, ...knightStyle, ...mcmStyle, ...lpsStyle, ...floydStyle, ...lcsStyle, ...editStyle, ...knapStyle, ...coinStyle, ...mrStyle, ...extraStyle }}
        title={`[${ri},${ci}] = ${isInf ? '∞' : val}`}
      >
        {display}
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      className="relative flex h-full w-full flex-col items-center justify-center overflow-hidden gap-1.5"
      style={{ padding: '10px 12px 8px' }}
    >
      {/* Header row: dimension + step badge */}
      <div className="flex w-full items-center justify-between flex-shrink-0 px-0.5">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono uppercase tracking-wider" style={{ color: '#3f3f46' }}>
            {rows} × {cols}
            {useVirtual && (
              <span className="ml-2 text-[9px]" style={{ color: '#92400e' }}>virtual</span>
            )}
          </span>
          {(() => {
            const sig = detectMatrixSignature(algorithmId);
            if (sig === 'none') return null;
            const lbl = signatureLabel(sig);
            const clr = signatureColor(sig);
            return (
              <span
                className="rounded px-1.5 py-0.5 text-[8px] font-bold tracking-widest"
                style={{ color: clr, border: `1px solid ${clr}33`, background: `${clr}0f` }}
              >
                {lbl}
              </span>
            );
          })()}
        </div>
        <span
          className="rounded px-2 py-0.5 text-[9px] font-bold tracking-widest"
          style={{ color: badge.color, border: `1px solid ${badge.color}33`, background: `${badge.color}0f` }}
        >
          {badge.label}
        </span>
      </div>

      {useVirtual ? (
        /* ── Virtual grid: scrollable container with absolute-positioned cells ── */
        <div
          ref={scrollRef}
          className="flex-1 overflow-auto w-full rounded-xl"
          style={{ background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(255,255,255,0.06)' }}
          onScroll={handleScroll}
        >
          <div className="relative" style={{ width: totalGridW, height: totalGridH }}>
            {/* Column headers */}
            {hasColH && (colHeaders ?? []).slice(
              virtualWindow?.colStart ?? 0,
              virtualWindow?.colEnd   ?? cols,
            ).map((h, relCi) => {
              const ci     = (virtualWindow?.colStart ?? 0) + relCi;
              const leftPx = (hasRowH ? stride : 0) + ci * stride + gap;
              return (
                <div
                  key={`ch-${ci}`}
                  className="absolute flex items-center justify-center overflow-hidden select-none"
                  style={{ width: cell, height: cell, top: gap, left: leftPx, ...headerStyle }}
                  title={String(h)}
                >
                  {String(h).slice(0, 5)}
                </div>
              );
            })}

            {/* Data rows */}
            {data.slice(
              virtualWindow?.rowStart ?? 0,
              virtualWindow?.rowEnd   ?? rows,
            ).map((row, relRi) => {
              const ri    = (virtualWindow?.rowStart ?? 0) + relRi;
              const topPx = (hasColH ? stride : 0) + ri * stride + gap;
              return (
                <React.Fragment key={`r-${ri}`}>
                  {hasRowH && (
                    <div
                      className="absolute flex items-center justify-center overflow-hidden select-none"
                      style={{ width: cell, height: cell, top: topPx, left: gap, ...headerStyle }}
                      title={String(rowHeaders?.[ri] ?? ri)}
                    >
                      {String(rowHeaders?.[ri] ?? ri).slice(0, 5)}
                    </div>
                  )}
                  {row.slice(
                    virtualWindow?.colStart ?? 0,
                    virtualWindow?.colEnd   ?? cols,
                  ).map((val, relCi) => {
                    const ci     = (virtualWindow?.colStart ?? 0) + relCi;
                    const leftPx = (hasRowH ? stride : 0) + ci * stride + gap;
                    return renderCell(ri, ci, val, { position: 'absolute', top: topPx, left: leftPx });
                  })}
                </React.Fragment>
              );
            })}
          </div>
          {/* SVG overlay for dependency arrows (virtual mode) */}
          {depArrows && depArrows.length > 0 && (
            <svg
              className="absolute pointer-events-none"
              width={totalGridW}
              height={totalGridH}
              style={{ overflow: 'visible', top: 0, left: 0 }}
            >
              <defs>
                <marker id="mv-arr-dep-v" viewBox="0 0 10 10" refX="9" refY="5"
                  markerWidth="5" markerHeight="5" orient="auto-start-reverse">
                  <polygon points="0,1 10,5 0,9" fill="#60a5fa" />
                </marker>
              </defs>
              {depArrows.map((a, i) => a
                ? <path key={`depv-${i}`} d={(a as React.ReactElement).props.d}
                    fill="none" stroke="#60a5fa" strokeWidth={1.5}
                    strokeDasharray="3 2" opacity={0.65} markerEnd="url(#mv-arr-dep-v)" />
                : null)}
            </svg>
          )}
        </div>
      ) : (
        /* ── Normal grid: CSS grid layout ── */
        <div
          className="relative rounded-xl overflow-hidden"
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${gridCols}, ${cell}px)`,
            gap: `${gap}px`,
            padding: `${gap}px`,
            background: 'rgba(0,0,0,0.35)',
            border: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          {hasColH && (
            <div
              className="flex items-center justify-center"
              style={{ width: cell, height: cell, ...headerStyle, visibility: hasRowH ? 'visible' : 'hidden' }}
            />
          )}
          {hasColH && (colHeaders ?? []).slice(0, cols).map((h, ci) => (
            <div
              key={`ch-${ci}`}
              className="flex items-center justify-center overflow-hidden select-none"
              style={{ width: cell, height: cell, ...headerStyle }}
              title={String(h)}
            >
              {String(h).slice(0, 5)}
            </div>
          ))}
          {data.map((row, ri) => (
            <React.Fragment key={`r-${ri}`}>
              {hasRowH && (
                <div
                  className="flex items-center justify-center overflow-hidden select-none"
                  style={{ width: cell, height: cell, ...headerStyle }}
                  title={String(rowHeaders?.[ri] ?? ri)}
                >
                  {String(rowHeaders?.[ri] ?? ri).slice(0, 5)}
                </div>
              )}
              {row.map((val, ci) => renderCell(ri, ci, val))}
            </React.Fragment>
          ))}

          {/* SVG overlay for dependency arrows */}
          {depArrows && depArrows.length > 0 && (
            <svg
              className="absolute inset-0 pointer-events-none"
              width={totalGridW}
              height={totalGridH}
              style={{ overflow: 'visible' }}
            >
              <defs>
                <marker id="mv-arr-dep" viewBox="0 0 10 10" refX="9" refY="5"
                  markerWidth="5" markerHeight="5" orient="auto-start-reverse">
                  <polygon points="0,1 10,5 0,9" fill="#60a5fa" />
                </marker>
              </defs>
              {depArrows}
            </svg>
          )}
        </div>
      )}

      {/* Explanation strip */}
      {explanation && (
        <div
          className="w-full flex-shrink-0 px-1 text-center text-[10px] truncate"
          style={{ color: '#52525b', lineHeight: '1.3' }}
          title={explanation}
        >
          {explanation}
        </div>
      )}
    </div>
  );
});

export default MatrixVisualizer;
